/**
 * Smart Prompting Utility
 * 
 * Suggests next best question based on profile completion.
 * Used to guide conversation flow and collect critical profile information.
 * 
 * @module utils/smartPrompting
 */

import type { MasterProfile } from '../types';

/**
 * Question suggestion with priority and metadata
 */
interface QuestionSuggestion {
  /** Condition that must be true for this question to be suggested */
  condition: boolean;
  /** Priority (higher = more important). Range: 1-10 */
  priority: number;
  /** The question text to ask the user */
  question: string;
  /** The profile field this question targets (dot notation) */
  field: string;
  /** Category for grouping questions */
  category: 'essential' | 'safety' | 'preference' | 'logistics' | 'conversion';
}

/**
 * Missing field information
 */
interface MissingField {
  /** Field path in dot notation (e.g., "skin.type") */
  field: string;
  /** Human-readable label in Korean */
  label: string;
}

/**
 * Profile validation result
 */
interface ValidationResult {
  /** Whether the profile is valid for the requested action */
  valid: boolean;
  /** Error message if invalid (Korean) */
  message?: string;
  /** List of missing fields if invalid */
  missingFields?: MissingField[];
}

/**
 * Gets the next best question to ask based on profile completion.
 * 
 * Questions are prioritized by:
 * 1. Essential profile fields (skin type, age, concerns)
 * 2. Safety fields (allergies)
 * 3. Conversion fields (address for sample delivery)
 * 
 * @param profile - Current user profile (can be null/undefined)
 * @param completion - Profile completion percentage (0-100)
 * @returns The highest priority question suggestion, or null if profile is complete
 * 
 * @example
 * ```typescript
 * const question = getNextBestQuestion(profile, 65);
 * if (question) {
 *   console.log(`Ask: ${question.question}`);
 * }
 * ```
 */
export function getNextBestQuestion(
  profile: MasterProfile | null | undefined,
  completion: number
): QuestionSuggestion | null {
  const questions: QuestionSuggestion[] = [
    {
      condition: !profile?.skin?.type,
      priority: 10,
      question: "피부 타입을 알려주시면 맞춤 추천이 가능해요. 건조/지성/복합성/민감성 중 어디에 가까우신가요?",
      field: "skin.type",
      category: "essential"
    },
    {
      condition: !profile?.identity?.ageGroup && !profile?.identity?.birthYear,
      priority: 9,
      question: "연령대를 알려주시겠어요? (20대, 30대, 40대, 50대, 60대+ 중 선택 / 최소 13세 이상) 더 적합한 제품을 추천해 드릴 수 있어요.",
      field: "identity.ageGroup",
      category: "essential"
    },
    {
      condition: !profile?.skin?.concerns || profile.skin.concerns.length === 0,
      priority: 8,
      question: "어떤 피부 고민이 있으신가요? (예: 여드름, 주름, 색소침착, 모공 등)",
      field: "skin.concerns",
      category: "essential"
    },
    {
      condition: !profile?.skin?.allergies || profile.skin.allergies.length === 0,
      priority: 7,
      question: "혹시 피부에 자극을 주는 성분이나 알러지가 있으신가요?",
      field: "skin.allergies",
      category: "safety"
    },
    {
      condition: !profile?.skin?.skinGoals || profile.skin.skinGoals.length === 0,
      priority: 6,
      question: "피부 관리의 주요 목표가 있으신가요? (예: 수분 보충, 미백, 주름 개선 등)",
      field: "skin.skinGoals",
      category: "preference"
    },
    {
      condition: !profile?.logistics?.address1,
      priority: 10,
      question: "프로필이 거의 완성되었어요! 🎉 무료 샘플을 보내드리고 싶은데, 배송 주소를 알려주시겠어요?",
      field: "logistics.address1",
      category: "conversion"
    },
    {
      condition: !profile?.logistics?.city,
      priority: 5,
      question: "어느 지역에 거주하시나요? 배송 정보를 위해 필요해요.",
      field: "logistics.city",
      category: "logistics"
    }
  ];

  // Filter questions that match conditions, sort by priority
  const availableQuestions = questions
    .filter(q => q.condition)
    .sort((a, b) => b.priority - a.priority);

  return availableQuestions[0] || null;
}

/**
 * Critical fields required for basic product recommendations.
 * Maps field path to Korean label.
 */
const CRITICAL_FIELDS: Record<string, string> = {
  'skin.type': '피부 타입',
  'identity.ageGroup': '연령대',
  'skin.concerns': '피부 고민',
};

/**
 * Gets list of missing critical fields from profile.
 * 
 * Critical fields are required for basic product recommendations.
 * 
 * @param profile - User profile to check
 * @returns Array of missing field objects with field path and Korean label
 * 
 * @example
 * ```typescript
 * const missing = getMissingCriticalFields(profile);
 * if (missing.length > 0) {
 *   console.log(`Missing: ${missing.map(m => m.label).join(', ')}`);
 * }
 * ```
 */
export function getMissingCriticalFields(
  profile: MasterProfile | null | undefined
): MissingField[] {
  const missing: MissingField[] = [];
  
  for (const [field, label] of Object.entries(CRITICAL_FIELDS)) {
    const keys = field.split('.');
    let value: unknown = profile;
    
    for (const key of keys) {
      value = (value as Record<string, unknown>)?.[key];
      if (value === undefined || value === null) break;
    }
    
    // Check if value is missing or empty array
    if (!value || (Array.isArray(value) && value.length === 0)) {
      missing.push({ field, label });
    }
  }
  
  return missing;
}

/**
 * Fields required for sample delivery.
 * Maps field path to Korean label.
 */
const DELIVERY_FIELDS: Record<string, string> = {
  'skin.type': '피부 타입',
  'identity.ageGroup': '연령대',
  'logistics.address1': '주소',
  'logistics.city': '도시',
  'logistics.postalCode': '우편번호',
};

/**
 * Gets list of missing fields required for sample delivery.
 * 
 * @param profile - User profile to check
 * @returns Array of missing field objects with field path and Korean label
 * 
 * @example
 * ```typescript
 * const missing = getMissingDeliveryFields(profile);
 * if (missing.length > 0) {
 *   // Prompt user for missing information
 * }
 * ```
 */
export function getMissingDeliveryFields(
  profile: MasterProfile | null | undefined
): MissingField[] {
  const missing: MissingField[] = [];
  
  for (const [field, label] of Object.entries(DELIVERY_FIELDS)) {
    const keys = field.split('.');
    let value: unknown = profile;
    
    for (const key of keys) {
      value = (value as Record<string, unknown>)?.[key];
      if (value === undefined || value === null) break;
    }
    
    if (!value) {
      missing.push({ field, label });
    }
  }
  
  return missing;
}

/**
 * Validates profile for sample request eligibility.
 * 
 * Checks that all required delivery fields are present.
 * 
 * @param profile - User profile to validate
 * @returns Validation result with valid flag, message, and missing fields
 * 
 * @example
 * ```typescript
 * const result = validateProfileForSample(profile);
 * if (!result.valid) {
 *   alert(result.message);
 * }
 * ```
 */
export function validateProfileForSample(
  profile: MasterProfile | null | undefined
): ValidationResult {
  const missing = getMissingDeliveryFields(profile);
  
  if (missing.length === 0) {
    return { valid: true };
  }
  
  const missingLabels = missing.map(m => m.label).join(', ');
  
  return {
    valid: false,
    message: `샘플 발송을 위해 ${missingLabels} 정보가 필요합니다.`,
    missingFields: missing
  };
}
